import { useEffect, useState, useRef, useCallback } from 'react'
import { useLazyQuery, OperationVariables } from '@apollo/client'

import { useToastNotification } from '@/components/compliance-assistant/ToastNotification'
import { getGraph as getGraphQuery, getEntityKey as getEntityKeyQuery } from '@/lib/graphQueries'
import { validateRut } from '@/lib/utils'
import { useRenderTopMenuChildComponent } from '@/providers/TopMenuChildrenProvider'
import { useGeneralSettingsStore, useSelectedNodeStore, useGraphData } from '@unholster/graphkit'

import EntityDetailPanel from './entity-detail-panel'
import Graph from './graph'
import createForceGraphData from './graph/force-graph-data'
import { ForceGraphInputType, NodeObjectType, NodeType, LinkType, categories } from './graph/types'
import TopMenu from './top-menu'
import Spinner from '@/assets/Spinner'

type GraphDataResponse = {
  getGraph: {
    nodes: NodeType[]
    edges: LinkType[]
  }
}

type EntityKeyResponse = {
  getEntityKey: {
    entityKey: string
    entityType: string
  }
}

interface FigureRef {
  centerAt: (x: number | undefined, y: number | undefined) => void
}

export default function GraphLab(): JSX.Element {
  // Graph
  const [categoryFilter, setCategoryFilter] = useState<string[]>([])
  const [selectedNode, setSelectedNode] = useState<NodeObjectType | null>(null)
  const [shownData, setShownData] = useState<ForceGraphInputType>({
    links: [],
    nodes: [],
  })
  const [queryEntityDepth, setQueryEntityDepth] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const { triggerToast } = useToastNotification()
  const { nodes, edges, updateData, resetData } = useGraphData()
  const { selectedNodeID, setSelectedNodeID } = useSelectedNodeStore()
  const setCategories = useGeneralSettingsStore((state) => state.setCategories)
  const { setTopMenuChildComponent } = useRenderTopMenuChildComponent()
  const parentDivRef = useRef<HTMLDivElement>(null)
  const figRef = useRef<FigureRef | null>(null)

  // Queries
  const [getGraphData, { data: newGraphData }] = useLazyQuery<
    GraphDataResponse,
    OperationVariables
  >(getGraphQuery)

  const [getEntityKey] = useLazyQuery<EntityKeyResponse, OperationVariables>(getEntityKeyQuery)

  const handleSearch = useCallback(
    async (value: string, depth: number) => {
      if (isLoading) return
      setIsLoading(true)

      try {
        resetData()
        setShownData({ links: [], nodes: [] })
        setSelectedNode(null)

        const searchField = validateRut(value) ? 'rut' : 'nombre_o_razon_social'
        const { data, error } = await getEntityKey({
          variables: { searchBy: JSON.stringify([{ [searchField]: value }]) },
        })

        if (error || !data?.getEntityKey) {
          triggerToast({ message: error?.message || 'Error obteniendo entidad' })
          setIsLoading(false)
          return
        }

        const { entityKey, entityType } = data.getEntityKey
        setSelectedNodeID(entityKey)
        setQueryEntityDepth(`${entityKey}-${depth}`) // This is used to reset the graph zoom
        const responseGraphData = await getGraphData({
          variables: {
            searchBy: JSON.stringify({ entity_key: entityKey }),
            entityType,
            depth: depth.toString(),
            retrieveSecondaryEntities: false,
          },
        })

        if (responseGraphData.error) {
          triggerToast({ message: responseGraphData.error.message })
          setIsLoading(false)
          return
        }
      } catch (error) {
        console.error('Search error:', error)
        triggerToast({ message: 'Error al buscar la entidad' })
      } finally {
        setIsLoading(false)
      }
    },
    [isLoading, getEntityKey, getGraphData, resetData, triggerToast, setSelectedNodeID]
  )

  useEffect(() => {
    setCategories(categories)
    getGraphData({
      variables: {
        searchBy: JSON.stringify({
          entity_key: 'bf40b175-3f1f-4a8b-82a5-c87166070b01', // Unholster
        }),
        entityType: 'persona_juridica',
        depth: '1',
        retrieveSecondaryEntities: false,
      },
    })

    setTimeout(() => {
      setTopMenuChildComponent(
        <TopMenu setCategoryFilter={setCategoryFilter} handleSearch={handleSearch} />
      )
    }, 0)
  }, [])

  useEffect(() => {
    if (!newGraphData || isLoading) return
    const { nodes: nodesData, edges: edgesData } = newGraphData.getGraph
    const forceGraphData = createForceGraphData(nodesData, edgesData)
    updateData(forceGraphData.nodes, forceGraphData.links)
  }, [newGraphData, updateData, isLoading])

  useEffect(() => {
    const node = nodes?.find((n) => n.id === selectedNodeID)
    if (node) {
      setSelectedNode(node)
    }
  }, [selectedNodeID, nodes])

  useEffect(() => {
    if (!shownData) return

    if (categoryFilter.length === 0) {
      setShownData({ links: edges, nodes })
    } else {
      const filteredNodes = nodes.filter((n) => !categoryFilter.includes(n.category as string))
      const filteredIds = new Set(filteredNodes.map((n) => n.id))
      const filteredLinks = edges.filter((l) => {
        const sourceId = typeof l.source === 'object' ? l.source.id : l.source
        const targetId = typeof l.target === 'object' ? l.target.id : l.target
        return filteredIds.has(sourceId) && filteredIds.has(targetId)
      })
      setShownData({ nodes: filteredNodes, links: filteredLinks })
    }
  }, [categoryFilter, edges, nodes])

  const handleLocalizeNode = useCallback(
    (node: NodeObjectType) => {
      const exactNode = nodes.find((n) => n.id === node.id)
      if (exactNode && figRef.current) {
        figRef.current.centerAt(exactNode.x ?? 0, exactNode.y ?? 0)
        setSelectedNodeID(node.id)
      }
    },
    [figRef, nodes, setSelectedNodeID]
  )

  return (
    <div className="relative w-full h-[calc(100%-136px)] mt-[136px]">
      <EntityDetailPanel
        selectedNode={selectedNode}
        handleLocalizeNode={handleLocalizeNode}
        setSelectedNodeID={setSelectedNodeID}
      />
      <div className="relative h-full w-full" ref={parentDivRef}>
        <Graph
          shownData={shownData}
          figRef={figRef}
          getGraphData={getGraphData}
          parentDivRef={parentDivRef}
          queryEntityDepth={queryEntityDepth}
          updateData={updateData}
        />
        {isLoading && (
          <div className="absolute flex inset-0 items-center justify-center pointer-events-none">
            <Spinner className="w-24 h-24 animate-spin" />
          </div>
        )}
      </div>
    </div>
  )
}
